import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * useNewOrderWatcher — polls /api/orders every `intervalMs` and detects orders
 * whose numeric part exceeds `lastSeenMax` (persisted in localStorage).
 *
 * BELL IMPLEMENTATION NOTES (v2 — Iter 15):
 * The previous version lazily created an AudioContext inside beep() but Chrome's
 * autoplay policy still blocked it on first fire. We now:
 *   1. Hold a single AudioContext in a ref — created lazily on the first user
 *      gesture (pointerdown/keydown anywhere on the page).
 *   2. If the context was auto-suspended, `resume()` runs synchronously inside
 *      the gesture (never on a timer).
 *   3. Expose `playTestBeep()` for a manual "🔊 Sesi test et" button so admin
 *      verifies audio works right after login, before any order has arrived.
 *   4. Mobile Safari requires the context to be *created* inside the gesture AND
 *      an oscillator played immediately. The unlock handler now plays a silent
 *      0.01-s tick to prime the pipeline so later beeps are instant.
 *
 * Returns: { newCount, newOrderIds, markAllSeen, refresh, playTestBeep, audioReady }
 */
const STORE_KEY = "ikz_last_seen_order_num";

function parseOrderNum(num) {
  if (!num) return 0;
  const m = String(num).match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
}

export function useNewOrderWatcher({ enabled = true, intervalMs = 15000 } = {}) {
  const [newCount, setNewCount] = useState(0);
  const [newOrderIds, setNewOrderIds] = useState([]);
  const [audioReady, setAudioReady] = useState(false);
  const lastSeenRef = useRef(parseInt(localStorage.getItem(STORE_KEY) || "0", 10));
  const titleRef = useRef(typeof document !== "undefined" ? document.title : "");
  const ctxRef = useRef(null);
  const prevCountRef = useRef(0);

  // Create / resume AudioContext inside the user gesture (Chrome Autoplay Policy)
  const ensureAudioContext = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!ctxRef.current) {
        ctxRef.current = new AC();
        // Prime the pipeline with a near-silent click so later beeps are instant
        const osc = ctxRef.current.createOscillator();
        const gain = ctxRef.current.createGain();
        gain.gain.value = 0.0001;
        osc.connect(gain).connect(ctxRef.current.destination);
        osc.start(0);
        osc.stop(ctxRef.current.currentTime + 0.01);
      }
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume();
      }
      setAudioReady(true);
      return ctxRef.current;
    } catch {
      return null;
    }
  };

  const doBeep = (volume = 0.4) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state === "suspended") return false;
    const now = ctx.currentTime;
    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + start);
      g.gain.exponentialRampToValueAtTime(volume, now + start + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    // Three-tone attention chime: C6 → E6 → G6
    tone(1047, 0.0, 0.18);
    tone(1319, 0.14, 0.18);
    tone(1568, 0.28, 0.26);
    return true;
  };

  const playTestBeep = () => {
    const ctx = ensureAudioContext();
    if (!ctx) {
      toast.error("Bu tarayıcı Web Audio desteklemiyor");
      return;
    }
    const ok = doBeep();
    if (ok) toast.success("🔊 Ses testi başarılı — yeni sipariş geldiğinde böyle duyacaksın", { duration: 2500 });
  };

  // Unlock AudioContext on first user gesture anywhere on the page
  useEffect(() => {
    const unlock = () => {
      ensureAudioContext();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const check = async () => {
    try {
      const { data } = await api.get("/orders");
      const sorted = (data || [])
        .map((o) => ({ ...o, _n: parseOrderNum(o.order_number) }))
        .sort((a, b) => b._n - a._n);
      const unseen = sorted.filter((o) => o._n > lastSeenRef.current);
      const alertable = unseen.filter((o) => !["delivered", "cancelled"].includes(o.status));
      const count = alertable.length;
      // Beep if count went up since last tick (and audio is ready)
      if (count > prevCountRef.current) {
        const added = count - prevCountRef.current;
        if (ctxRef.current && ctxRef.current.state === "running") {
          doBeep();
          toast.success(`🔔 ${added} yeni sipariş geldi!`, { duration: 3500 });
        } else {
          toast.warning("🔕 Yeni sipariş var ama ses kilitli — '🔊 Sesi aç' düğmesine bas", { duration: 5000 });
        }
      }
      prevCountRef.current = count;
      setNewCount(count);
      setNewOrderIds(alertable.map((o) => o.id));
      document.title = count > 0
        ? `(${count}) Yeni Sipariş · İkizler Panel`
        : (titleRef.current || "İkizler Panel");
    } catch {}
  };

  useEffect(() => {
    if (!enabled) return;
    check();
    const i = setInterval(check, intervalMs);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs]);

  const markAllSeen = (orders) => {
    const maxN = Math.max(
      lastSeenRef.current,
      ...(orders || []).map((o) => parseOrderNum(o.order_number))
    );
    lastSeenRef.current = maxN;
    try { localStorage.setItem(STORE_KEY, String(maxN)); } catch {}
    prevCountRef.current = 0;
    setNewCount(0);
    setNewOrderIds([]);
    document.title = titleRef.current || "İkizler Panel";
  };

  return { newCount, newOrderIds, markAllSeen, refresh: check, playTestBeep, audioReady };
}
